import React from 'react'

import { Box, FormHelperText, Stack, Typography } from '@mui/material'

import { Alert, Button, Modal } from '@repo/ui'

import { EmailChipInput } from 'components'
import { useSnackbar } from 'contexts'
import { useInviteDemoBrandUser } from 'hooks/api'
import { DemoListItem } from 'services/demosApi'
import { getErrorMessage } from 'utils'

interface InviteDemoUserModalProps {
  demo: DemoListItem | null
  open: boolean
  onClose: () => void
}

/**
 * VRG-361 Slice 2 — invite a real BRAND admin into a demo.
 *
 * This is the action the takeover was waiting on. Until it existed a demo could not
 * receive a brand user at all, so "Convert to real account" could never satisfy its
 * precondition and had to be shown disabled with a tooltip explaining a state the AE
 * had no way to resolve.
 *
 * ## Why this is its own modal and not `InviteCompanyUserModal`
 *
 * §28 says reuse before hand-rolling, and the reusable PARTS are reused: `Modal`,
 * `Button`, `Alert` from `@repo/ui`, and the shared `EmailChipInput` (which owns
 * comma/space/newline splitting, paste handling and validation). What is NOT reused
 * is that modal as a whole, for two reasons that are about the surface rather than
 * about styling:
 *
 * 1. **It is bound to the caller's OWN company.** It posts to
 *    `/companies/admin/me/invite-admins` and loads `useCompanyRoles()`, which reads
 *    the roles of whatever company the session is scoped to. An internal AE acting
 *    on a demo is not scoped to that demo, so both would be wrong here.
 * 2. **It has a ROLE PICKER, and this one must not.** The server fixes the role at
 *    `company_admin` and refuses to take one from the body — because the shared
 *    invite path defaults an omitted role to `platform_manager` for internal
 *    callers, so a role a client could choose is precisely the thing that would let
 *    the brand's own decision-maker be invited as Vera staff. A picker here would
 *    advertise a choice the API does not offer.
 *
 * ## The copy names what actually happens, because two of the three are surprising
 *
 * The email is sent by WorkOS, not by Vera, so it does not appear in Mailpit and its
 * sender is the WorkOS tenant. The membership row is written IMMEDIATELY, before
 * anybody opens anything. And signing in is what starts the console trial clock and
 * unblocks conversion. An AE who assumes any of those works differently tells the
 * prospect something untrue on the same call.
 */
export const InviteDemoUserModal = ({ demo, open, onClose }: InviteDemoUserModalProps) => {
  const { open: notify } = useSnackbar()
  const { mutate: invite, isPending } = useInviteDemoBrandUser()
  const [emails, setEmails] = React.useState<string[]>([])
  const [submitError, setSubmitError] = React.useState<string | null>(null)

  const handleClose = () => {
    setEmails([])
    setSubmitError(null)
    onClose()
  }

  const handleInvite = () => {
    if (!demo || emails.length === 0) return
    setSubmitError(null)
    invite(
      { companyId: demo.companyId, emails },
      {
        onSuccess: (result) => {
          // The row's invited count moves, so the toast does not repeat it. What the
          // row cannot show is the part with a next action attached: nothing arrives
          // in Mailpit, and conversion stays blocked until they actually sign in.
          notify({
            type: 'success',
            title: `Invited ${result.totalInvited} ${
              result.totalInvited === 1 ? 'person' : 'people'
            } to ${result.companyName}`,
            text:
              'WorkOS sends the invitation email directly, so it will not show up in Mailpit. ' +
              'Converting this demo stays blocked until one of them signs in.',
          })
          handleClose()
        },
        onError: (error) => {
          // Inline rather than a toast. Each refusal names a different missing
          // precondition (not a demo, already taken over, a per-address WorkOS
          // failure) and the AE's next move differs for each, so it has to stay on
          // screen beside the button that produced it.
          setSubmitError(getErrorMessage(error))
        },
      }
    )
  }

  return (
    <Modal
      title="Invite a brand admin"
      open={open}
      onClose={handleClose}
      actions={
        <>
          <Button disableElevation variant="secondary" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            disableElevation
            onClick={handleInvite}
            loading={isPending}
            disabled={emails.length === 0}
          >
            Send invite
          </Button>
        </>
      }
    >
      <Stack spacing={4}>
        {submitError && <Alert severity="error" description={submitError} />}

        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          They join <strong>{demo?.companyName ?? 'this brand'}</strong> as a company admin
          and get the demo you built for them. Vera staff keep their support access until
          you convert the account.
        </Typography>

        <Box>
          <Stack direction="column" gap={1}>
            <EmailChipInput label="Email addresses" value={emails} onChange={setEmails} />
            <FormHelperText>
              To invite multiple people separate the emails by comma, space or new line
            </FormHelperText>
          </Stack>
        </Box>

        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          The invitation is sent by WorkOS, so it will not appear in Mailpit. They show
          as invited straight away, but converting this demo to a real account stays
          blocked until one of them actually signs in.
        </Typography>
      </Stack>
    </Modal>
  )
}
